import React, { useState, useEffect, useCallback } from 'react';
import { Plus, AlertCircle, Trash2, CheckCircle, Clock, RotateCcw, Users, Skull } from 'lucide-react';
import api from '../utils/api';
import { formatDateBR, todayLocalISO } from '../utils/formatDate';
import toast from 'react-hot-toast';
import AnimalSearchSelect from '../components/AnimalSearchSelect';

const TIPOS = [
  { value: 'vacina', label: 'Vacina', consomeEstoque: true, permiteEmMassa: true },
  { value: 'vermifugo', label: 'Vermífugo', consomeEstoque: true, permiteEmMassa: true },
  { value: 'antibiotico', label: 'Antibiótico', consomeEstoque: true },
  { value: 'antiparasitario', label: 'Antiparasitário', consomeEstoque: true },
  { value: 'tratamento', label: 'Tratamento', consomeEstoque: true },
  { value: 'cirurgia', label: 'Cirurgia', consomeEstoque: false },
  { value: 'exame', label: 'Exame', consomeEstoque: false },
  { value: 'corte_casco', label: 'Corte de Casco', consomeEstoque: false },
  { value: 'tosquia', label: 'Tosquia', consomeEstoque: false },
  { value: 'outro', label: 'Outro', consomeEstoque: false },
];

const TIPO_BADGE = { vacina:'badge-blue', vermifugo:'badge-green', antibiotico:'badge-orange', antiparasitario:'badge-yellow', cirurgia:'badge-red', exame:'badge-gray', tratamento:'badge-orange', corte_casco:'badge-gray', tosquia:'badge-gray', outro:'badge-gray' };

export default function Intervencoes() {
  const [data, setData] = useState([]);
  const [animais, setAnimais] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [filtro, setFiltro] = useState({ animal_id:'', tipo:'', status:'', data_inicio:'', data_fim:'' });
  const [sort, setSort] = useState({ col:'data', dir:'desc' });
  const [showModal, setShowModal] = useState(false);
  const [showObitoModal, setShowObitoModal] = useState(false);
  const [showMassaModal, setShowMassaModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [salvandoMassa, setSalvandoMassa] = useState(false);
  const [form, setForm] = useState({ animal_id:'', data:todayLocalISO(), tipo:'vacina', tipo_exame:'', produto_id:'', dose:'', unidade_dose:'ml', via_administracao:'', carencia_dias:'', diagnostico:'', observacoes:'', status:'realizada', data_proxima_acao:'', tipo_proxima_acao:'' });
  const [formMassa, setFormMassa] = useState({ data:todayLocalISO(), tipo:'vacina', produto_id:'', dose:'', unidade_dose:'ml', via_administracao:'', carencia_dias:'', observacoes:'', lote_id:'' });
  const [formObito, setFormObito] = useState({ animal_id:'', data_obito:todayLocalISO(), motivo_obito:'desconhecido', observacoes:'' });

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data:d } = await api.get('/sanitario/intervencoes', { params:filtro }); setData(d); }
    finally { setLoading(false); }
  }, [filtro]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/animais', { params:{ situacao:'ativo', limit:500 } }).then(r => setAnimais(r.data.data || r.data));
    api.get('/estoque/produtos').then(r => setProdutos(r.data));
    api.get('/lotes').then(r => setLotes(r.data));
  }, []);

  // ── Individual ──
  const salvar = async () => {
    try {
      await api.post('/sanitario/intervencoes', form);
      toast.success('Intervenção registrada!');
      setShowModal(false);
      setForm(p => ({ ...p, animal_id:'', diagnostico:'', observacoes:'', dose:'', tipo_exame:'' }));
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Erro'); }
  };

  // ── Em Massa ──
  const salvarEmMassa = async () => {
    const qtd = formMassa.lote_id
      ? animais.filter(a => String(a.lote_id) === String(formMassa.lote_id)).length
      : animais.length;
    if (!window.confirm(`Confirma aplicar ${formMassa.tipo} em ${qtd > 0 ? qtd : 'todos os'} animais${formMassa.lote_id ? ' deste lote' : ' ativos'}?\n\nDose por animal: ${formMassa.dose || '—'} ${formMassa.unidade_dose}\nDose total estimada: ${formMassa.dose ? (formMassa.dose * qtd).toFixed(1) : '—'} ${formMassa.unidade_dose}`)) return;
    setSalvandoMassa(true);
    try {
      const { data:result } = await api.post('/sanitario/intervencoes/massa', formMassa);
      toast.success(result.message);
      setShowMassaModal(false);
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Erro na intervenção em massa'); }
    finally { setSalvandoMassa(false); }
  };

  const registrarObito = async () => {
    if (!formObito.animal_id) return toast.error('Selecione o animal');
    try {
      const { data } = await api.patch(`/animais/${formObito.animal_id}/obito`, formObito);
      toast.success(data.tempo_vida_dias != null
        ? `Óbito registrado. Tempo de vida: ${data.tempo_vida_dias} dias.`
        : 'Óbito registrado.');
      setShowObitoModal(false);
      setFormObito({ animal_id:'', data_obito:todayLocalISO(), motivo_obito:'desconhecido', observacoes:'' });
      api.get('/animais', { params:{ situacao:'cabana', limit:500 } }).then(r => setAnimais(r.data.data || r.data));
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Erro ao registrar óbito'); }
  };

  const excluir = async (id) => {
    if (!window.confirm('Excluir esta intervenção? O estoque será estornado.')) return;
    try { await api.delete(`/sanitario/intervencoes/${id}`); toast.success('Excluída'); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Erro'); }
  };

  const concluir = async (id) => {
    try { await api.put(`/sanitario/intervencoes/${id}/concluir`); toast.success('Realizada!'); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Erro'); }
  };

  const reabrir = async (id) => {
    try { await api.put(`/sanitario/intervencoes/${id}/reabrir`); toast.success('Reaberta'); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Erro'); }
  };

  const tipoAtual = TIPOS.find(t => t.value === form.tipo);
  const mostrarProduto = tipoAtual?.consomeEstoque ?? true;
  // "Via" só para intervenções medicamentosas (mesma regra de consumo de estoque).
  const mostrarVia = mostrarProduto;
  const isExame = form.tipo === 'exame';
  const tipoMassa = TIPOS.find(t => t.value === formMassa.tipo);

  const produtosFiltrados = (tipo) => produtos.filter(p => {
    if (tipo === 'vacina') return p.tipo === 'vacina';
    if (tipo === 'vermifugo') return p.tipo === 'vermifugo';
    return true;
  });

  const toggleSort = (col) => setSort(s => ({ col, dir: s.col === col && s.dir === 'asc' ? 'desc' : 'asc' }));
  const sortedData = [...data].sort((a, b) => {
    let va = a[sort.col], vb = b[sort.col];
    if (!va && !vb) return 0; if (!va) return 1; if (!vb) return -1;
    if (typeof va === 'string') { va = va.toLowerCase(); vb = (vb||'').toLowerCase(); }
    const r = va < vb ? -1 : va > vb ? 1 : 0;
    return sort.dir === 'asc' ? r : -r;
  });
  const Th = ({ col, children }) => (
    <th onClick={() => toggleSort(col)} style={{ cursor:'pointer', userSelect:'none', whiteSpace:'nowrap' }}>
      {children}{sort.col===col ? (sort.dir==='asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );

  const totalCarencias = data.filter(d => d.data_carencia_fim && new Date(d.data_carencia_fim) >= new Date()).length;
  const totalPendentes = data.filter(d => d.status === 'pendente').length;

  // Contar animais afetados pela intervenção em massa
  const animaisMassa = formMassa.lote_id
    ? animais.filter(a => String(a.lote_id) === String(formMassa.lote_id)).length
    : animais.length;

  return (
    <div>
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <h1>Intervenções Sanitárias</h1>
          <p>{data.length} registros · {totalPendentes} pendentes · {totalCarencias} carências ativas</p>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn btn-secondary" onClick={() => setShowObitoModal(true)}
            style={{ background:'#FEF2F2', color:'#B91C1C', border:'1px solid #FECACA' }}>
            <Skull size={16} /> Registrar Óbito
          </button>
          <button className="btn btn-secondary" onClick={() => { setFormMassa(p => ({ ...p, data:todayLocalISO() })); setShowMassaModal(true); }}
            style={{ background:'#EFF6FF', color:'#1D4ED8', border:'1px solid #BFDBFE' }}>
            <Users size={16} /> Intervenção em Massa
          </button>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <Plus size={16} /> Nova Individual
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="card" style={{ marginBottom:16 }}>
        <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
          <div style={{ flex:2, minWidth:220 }}>
            <AnimalSearchSelect value={filtro.animal_id} onChange={id => setFiltro(p => ({ ...p, animal_id:id }))} emptyLabel="Todos os animais" />
          </div>
          <select value={filtro.tipo} onChange={e => setFiltro(p => ({ ...p, tipo:e.target.value }))} style={{ width:'auto' }}>
            <option value="">Todos os tipos</option>
            {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <select value={filtro.status} onChange={e => setFiltro(p => ({ ...p, status:e.target.value }))} style={{ width:'auto' }}>
            <option value="">Todos os status</option>
            <option value="pendente">Pendente</option>
            <option value="realizada">Realizada</option>
          </select>
          <input type="date" value={filtro.data_inicio} onChange={e => setFiltro(p => ({ ...p, data_inicio:e.target.value }))} style={{ width:150 }} />
          <input type="date" value={filtro.data_fim} onChange={e => setFiltro(p => ({ ...p, data_fim:e.target.value }))} style={{ width:150 }} />
        </div>
      </div>

      {/* Tabela */}
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        <table>
          <thead><tr><Th col="brinco">Animal</Th><Th col="data">Data</Th><Th col="tipo">Tipo</Th><Th col="produto_nome">Produto</Th><Th col="dose">Dose</Th><Th col="data_carencia_fim">Carência até</Th><Th col="status">Status</Th><Th col="diagnostico">Diagnóstico</Th><th></th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={9} style={{ textAlign:'center', padding:40 }}>Carregando...</td></tr>
            : sortedData.length === 0 ? <tr><td colSpan={9}><div className="empty-state"><p>Nenhuma intervenção</p></div></td></tr>
            : sortedData.map(d => {
              const isPendente = d.status === 'pendente';
              const isRealizada = d.status === 'realizada';
              return (
                <tr key={d.id} style={{ background:isPendente ? '#FFFBEB' : 'inherit' }}>
                  <td><strong style={{ color:'var(--color-primary)' }}>{d.brinco}</strong>{d.animal_nome && ` — ${d.animal_nome}`}</td>
                  <td>{formatDateBR(d.data)}</td>
                  <td><span className={`badge ${TIPO_BADGE[d.tipo]||'badge-gray'}`}>{d.tipo}{d.tipo === 'exame' && d.tipo_exame ? ` · ${d.tipo_exame}` : ''}</span></td>
                  <td>{d.produto_nome || '—'}</td>
                  <td style={{ fontSize:13 }}>{d.dose ? `${d.dose} ${d.unidade_dose||''}` : '—'}</td>
                  <td>
                    {d.data_carencia_fim ? (
                      <span style={{ display:'flex', alignItems:'center', gap:4, fontSize:13, color: new Date(d.data_carencia_fim) >= new Date() ? 'var(--color-warning)' : 'var(--text-secondary)' }}>
                        {new Date(d.data_carencia_fim) >= new Date() && <AlertCircle size={13} />}
                        {formatDateBR(d.data_carencia_fim)}
                      </span>
                    ) : '—'}
                  </td>
                  <td>
                    <span className={`badge ${isPendente ? 'badge-yellow' : isRealizada ? 'badge-green' : 'badge-gray'}`}
                      style={{ display:'inline-flex', alignItems:'center', gap:4 }}>
                      {isPendente ? <Clock size={11}/> : isRealizada ? <CheckCircle size={11}/> : null}
                      {isPendente ? 'Pendente' : isRealizada ? 'Realizada' : d.status || '—'}
                    </span>
                  </td>
                  <td style={{ fontSize:13, color:'var(--text-secondary)', maxWidth:150 }}>{d.diagnostico || d.observacoes || '—'}</td>
                  <td style={{ whiteSpace:'nowrap' }}>
                    <div style={{ display:'flex', gap:4 }}>
                      {isPendente && <button className="btn btn-secondary btn-sm" onClick={() => concluir(d.id)} style={{ color:'#16A34A', padding:'4px 8px', display:'flex', alignItems:'center', gap:3, fontSize:12 }}><CheckCircle size={13}/> Realizar</button>}
                      {isRealizada && <button className="btn btn-secondary btn-sm" onClick={() => reabrir(d.id)} style={{ padding:'4px 8px' }}><RotateCcw size={13}/></button>}
                      <button className="btn btn-secondary btn-sm" onClick={() => excluir(d.id)} style={{ color:'#DC2626', padding:'4px 8px' }}><Trash2 size={14}/></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ═══ MODAL INDIVIDUAL ═══ */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-header"><h2>Nova Intervenção Individual</h2><button className="btn btn-secondary btn-sm" onClick={() => setShowModal(false)}>✕</button></div>
            <div className="modal-body">
              <div className="form-group"><label>Animal *</label>
                <AnimalSearchSelect value={form.animal_id} onChange={id => setForm(p => ({ ...p, animal_id:id }))} required />
              </div>
              <div className="form-row">
                <div className="form-group"><label>Data *</label><input type="date" value={form.data} onChange={e => setForm(p => ({ ...p, data:e.target.value }))} /></div>
                <div className="form-group"><label>Tipo *</label>
                  <select value={form.tipo} onChange={e => setForm(p => ({ ...p, tipo:e.target.value, produto_id:'', dose:'', tipo_exame:'' }))}>
                    {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Status *</label>
                  <select value={form.status} onChange={e => setForm(p => ({ ...p, status:e.target.value }))}>
                    <option value="realizada">Realizada</option>
                    <option value="pendente">Pendente</option>
                  </select>
                </div>
                {isExame && (
                  <div className="form-group"><label>Tipo de exame *</label>
                    <select value={form.tipo_exame} onChange={e => setForm(p => ({ ...p, tipo_exame:e.target.value }))}>
                      <option value="">Selecione</option>
                      <option value="sangue">Sangue</option>
                      <option value="opg">OPG</option>
                      <option value="outro">Outro</option>
                    </select>
                  </div>
                )}
              </div>
              {mostrarProduto && (
                <>
                  <div className="form-group"><label>Produto</label>
                    <select value={form.produto_id} onChange={e => {
                      const prod = produtos.find(p => p.id === parseInt(e.target.value));
                      setForm(p => ({ ...p, produto_id:e.target.value, carencia_dias:prod?.periodo_carencia_dias || p.carencia_dias }));
                    }}>
                      <option value="">Selecione (opcional)</option>
                      {produtosFiltrados(form.tipo).map(p => <option key={p.id} value={p.id}>{p.nome} (estoque: {p.estoque_atual} {p.unidade})</option>)}
                    </select>
                  </div>
                  <div className="form-row-3">
                    <div className="form-group"><label>Dose</label><input type="number" step="0.1" value={form.dose} onChange={e => setForm(p => ({ ...p, dose:e.target.value }))} /></div>
                    <div className="form-group"><label>Unidade</label>
                      <select value={form.unidade_dose} onChange={e => setForm(p => ({ ...p, unidade_dose:e.target.value }))}>
                        {['ml','mg','g','comprimido','sachê','UI'].map(u => <option key={u}>{u}</option>)}
                      </select>
                    </div>
                    <div className="form-group"><label>Carência (dias)</label><input type="number" value={form.carencia_dias} onChange={e => setForm(p => ({ ...p, carencia_dias:e.target.value }))} /></div>
                  </div>
                </>
              )}
              {mostrarVia && (
                <div className="form-group"><label>Via</label>
                  <select value={form.via_administracao} onChange={e => setForm(p => ({ ...p, via_administracao:e.target.value }))}>
                    <option value="">Selecione</option>
                    {['IM (intramuscular)','SC (subcutânea)','IV (intravenosa)','Oral','Tópico','Pour-on'].map(v => <option key={v}>{v}</option>)}
                  </select>
                </div>
              )}
              <div className="form-group"><label>{isExame ? 'Resultado / descrição do exame' : 'Diagnóstico / Motivo'}</label><textarea rows={2} value={form.diagnostico} onChange={e => setForm(p => ({ ...p, diagnostico:e.target.value }))} placeholder={isExame ? 'Ex: OPG 1200 ovos/g; ou descreva o exame quando "Outro".' : ''} /></div>
              <div className="form-group"><label>Observações</label><textarea rows={2} value={form.observacoes} onChange={e => setForm(p => ({ ...p, observacoes:e.target.value }))} /></div>
              <div style={{ borderTop:'1px solid var(--border-color)', paddingTop:14, marginTop:4 }}>
                <div style={{ fontSize:13, fontWeight:600, color:'var(--text-secondary)', marginBottom:10, textTransform:'uppercase', letterSpacing:0.5 }}>Próxima ação</div>
                <div className="form-row">
                  <div className="form-group"><label>Data da próxima ação</label><input type="date" value={form.data_proxima_acao} onChange={e => setForm(p => ({ ...p, data_proxima_acao:e.target.value }))} /></div>
                  <div className="form-group"><label>Tipo da próxima ação</label>
                    <select value={form.tipo_proxima_acao} onChange={e => setForm(p => ({ ...p, tipo_proxima_acao:e.target.value }))}>
                      <option value="">Selecione</option>
                      {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                </div>
                {form.data_proxima_acao && <div style={{ fontSize:12, color:'var(--color-warning)', marginTop:-6 }}>⚠ A próxima ação aparecerá na Agenda Consolidada</div>}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={salvar} disabled={!form.animal_id || (isExame && !form.tipo_exame)}>Registrar</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ MODAL ÓBITO ═══ */}
      {showObitoModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowObitoModal(false)}>
          <div className="modal" style={{ maxWidth:520 }}>
            <div className="modal-header" style={{ background:'#FEF2F2', borderBottom:'2px solid #FECACA' }}>
              <h2 style={{ display:'flex', alignItems:'center', gap:8 }}><Skull size={20} color="#B91C1C" /> Registrar Óbito</h2>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowObitoModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group"><label>Animal *</label>
                <AnimalSearchSelect value={formObito.animal_id} onChange={id => setFormObito(p => ({ ...p, animal_id:id }))} situacao="cabana" required />
              </div>
              {formObito.animal_id && (() => {
                const animal = animais.find(a => String(a.id) === String(formObito.animal_id));
                const dias = animal?.data_nascimento && formObito.data_obito
                  ? Math.max(0, Math.floor((new Date(formObito.data_obito + 'T12:00:00') - new Date(animal.data_nascimento.split('T')[0] + 'T12:00:00')) / 86400000))
                  : null;
                return dias == null ? null : (
                  <div style={{ padding:'10px 14px', background:dias < 30 ? '#FEF3C7' : '#F0FDF4', border:`1px solid ${dias < 30 ? '#FBBF24' : '#BBF7D0'}`, borderRadius:8, marginBottom:12, fontSize:13 }}>
                    Tempo de vida: <strong>{dias} dias</strong>{dias < 30 ? ' · Óbito Precoce' : ''}
                  </div>
                );
              })()}
              <div className="form-row">
                <div className="form-group"><label>Data do óbito *</label>
                  <input type="date" value={formObito.data_obito} max={todayLocalISO()} onChange={e => setFormObito(p => ({ ...p, data_obito:e.target.value }))} />
                </div>
                <div className="form-group"><label>Motivo *</label>
                  <select value={formObito.motivo_obito} onChange={e => setFormObito(p => ({ ...p, motivo_obito:e.target.value }))}>
                    <option value="verminose">Verminose</option>
                    <option value="bicheira">Bicheira</option>
                    <option value="desconhecido">Desconhecido</option>
                  </select>
                </div>
              </div>
              <div className="form-group"><label>Observações</label>
                <textarea rows={3} value={formObito.observacoes} onChange={e => setFormObito(p => ({ ...p, observacoes:e.target.value }))} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowObitoModal(false)}>Cancelar</button>
              <button className="btn btn-danger" onClick={registrarObito}>Registrar Óbito</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ MODAL EM MASSA ═══ */}
      {showMassaModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowMassaModal(false)}>
          <div className="modal" style={{ maxWidth:560 }}>
            <div className="modal-header" style={{ background:'#EFF6FF', borderBottom:'2px solid #BFDBFE' }}>
              <h2 style={{ display:'flex', alignItems:'center', gap:8 }}>
                <Users size={20} color="#1D4ED8" /> Intervenção em Massa
              </h2>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowMassaModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              {/* Aviso */}
              <div style={{ padding:'12px 16px', background:'#FEF3C7', border:'1px solid #FDE68A', borderRadius:8, marginBottom:16, fontSize:13 }}>
                <strong>⚠️ Atenção:</strong> Esta ação vai registrar a intervenção para <strong>{animaisMassa}</strong> {formMassa.lote_id ? 'animais do lote selecionado' : 'animais ativos'} de uma vez.
              </div>

              <div className="form-row">
                <div className="form-group"><label>Tipo *</label>
                  <select value={formMassa.tipo} onChange={e => setFormMassa(p => ({ ...p, tipo:e.target.value, produto_id:'' }))}>
                    <option value="vacina">Vacina</option>
                    <option value="vermifugo">Vermífugo</option>
                  </select>
                </div>
                <div className="form-group"><label>Data *</label>
                  <input type="date" value={formMassa.data} onChange={e => setFormMassa(p => ({ ...p, data:e.target.value }))} />
                </div>
              </div>

              <div className="form-group"><label>Lote (opcional — vazio = todos os animais ativos)</label>
                <select value={formMassa.lote_id} onChange={e => setFormMassa(p => ({ ...p, lote_id:e.target.value }))}>
                  <option value="">Todos os animais ativos</option>
                  {lotes.map(l => <option key={l.id} value={l.id}>{l.nome}</option>)}
                </select>
              </div>

              <div className="form-group"><label>Produto</label>
                <select value={formMassa.produto_id} onChange={e => {
                  const prod = produtos.find(p => p.id === parseInt(e.target.value));
                  setFormMassa(p => ({ ...p, produto_id:e.target.value, carencia_dias:prod?.periodo_carencia_dias || p.carencia_dias }));
                }}>
                  <option value="">Selecione</option>
                  {produtosFiltrados(formMassa.tipo).map(p => <option key={p.id} value={p.id}>{p.nome} (estoque: {p.estoque_atual} {p.unidade})</option>)}
                </select>
              </div>

              <div className="form-row-3">
                <div className="form-group"><label>Dose / animal</label>
                  <input type="number" step="0.1" value={formMassa.dose} onChange={e => setFormMassa(p => ({ ...p, dose:e.target.value }))} placeholder="Ex: 2" />
                </div>
                <div className="form-group"><label>Unidade</label>
                  <select value={formMassa.unidade_dose} onChange={e => setFormMassa(p => ({ ...p, unidade_dose:e.target.value }))}>
                    {['ml','mg','g','comprimido','sachê','UI'].map(u => <option key={u}>{u}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Carência (dias)</label>
                  <input type="number" value={formMassa.carencia_dias} onChange={e => setFormMassa(p => ({ ...p, carencia_dias:e.target.value }))} />
                </div>
              </div>

              <div className="form-group"><label>Via</label>
                <select value={formMassa.via_administracao} onChange={e => setFormMassa(p => ({ ...p, via_administracao:e.target.value }))}>
                  <option value="">Selecione</option>
                  {['IM (intramuscular)','SC (subcutânea)','IV (intravenosa)','Oral','Tópico','Pour-on'].map(v => <option key={v}>{v}</option>)}
                </select>
              </div>

              <div className="form-group"><label>Observações</label>
                <textarea rows={2} value={formMassa.observacoes} onChange={e => setFormMassa(p => ({ ...p, observacoes:e.target.value }))} placeholder="Ex: Campanha de vacinação contra clostridiose" />
              </div>

              {/* Resumo */}
              {formMassa.dose && (
                <div style={{ padding:'12px 16px', background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:8, fontSize:13 }}>
                  <strong>Resumo:</strong> {formMassa.dose} {formMassa.unidade_dose} × {animaisMassa} animais = <strong>{(formMassa.dose * animaisMassa).toFixed(1)} {formMassa.unidade_dose} total</strong>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowMassaModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={salvarEmMassa} disabled={salvandoMassa || !formMassa.data}
                style={{ background:'#1D4ED8' }}>
                {salvandoMassa ? 'Aplicando...' : `Aplicar em ${animaisMassa} animais`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
